/**
 * EmailSearch — collapsible search panel (input + status/responsible filters).
 * Search syntax help (? icon) sits to the right of the search field.
 */

import { h } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import { Popover } from 'bootstrap'
import { SEARCH_DEBOUNCE_MS, buildSearchHelpHtml } from '@tshmanager/email-shared'

const SEARCH_HELP_HTML = buildSearchHelpHtml()

export function EmailSearchPanel({ store, i18n }) {
  const searchText  = store.searchText.value
  const advFilters  = store.advancedFilters.value
  const debounceTimer = useRef(null)
  const helpBtnRef = useRef(null)

  useEffect(() => () => clearTimeout(debounceTimer.current), [])

  useEffect(() => {
    const el = helpBtnRef.current
    if (!el) return
    if (el._bsPopover) {
      el._bsPopover.dispose()
      el._bsPopover = null
    }
    el._bsPopover = new Popover(el)
    return () => {
      el._bsPopover?.dispose()
      el._bsPopover = null
    }
  }, [])

  function handleSearchInput(e) {
    store.setSearchText(e.target.value)
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => store.fetchEmails(1), SEARCH_DEBOUNCE_MS)
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter') {
      clearTimeout(debounceTimer.current)
      store.fetchEmails(1)
    }
    if (e.key === 'Escape') {
      store.setSearchText('')
      store.fetchEmails(1)
    }
  }

  function handleAdvancedFilter(key, value) {
    store.setAdvancedFilter(key, value)
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => store.fetchEmails(1), SEARCH_DEBOUNCE_MS)
  }

  return (
    <div class="email-search-panel px-2 py-1">
      <div class="email-search-input-row d-flex align-items-center gap-1">
        <div class="email-search-input-wrap input-group input-group-sm flex-grow-1 min-w-0">
          <span class="input-group-text border-0 bg-transparent ps-1 pe-0">
            <i class="bi bi-search text-muted" style="font-size:0.8rem" />
          </span>
          <input
            type="text"
            class="form-control form-control-sm border-0 shadow-none bg-transparent"
            placeholder={i18n?.t('emails.search_placeholder')}
            value={searchText}
            data-email-search-input=""
            onInput={handleSearchInput}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          {searchText && (
            <button
              type="button"
              class="btn btn-link btn-sm text-muted p-0 pe-1"
              onClick={() => { store.setSearchText(''); store.fetchEmails(1) }}
            >
              <i class="bi bi-x" />
            </button>
          )}
        </div>
        <button
          type="button"
          class="btn btn-sm btn-link p-0 flex-shrink-0 text-muted email-search-help-btn"
          ref={helpBtnRef}
          data-bs-toggle="popover"
          data-bs-trigger="focus"
          data-bs-html="true"
          data-bs-title={i18n?.t('emails.search_help_title')}
          data-bs-content={SEARCH_HELP_HTML}
          data-bs-placement="bottom"
          title={i18n?.t('emails.search_help_title')}
          aria-label={i18n?.t('emails.search_help_title')}
        >
          <i class="bi bi-question-circle" style="font-size:0.95rem" />
        </button>
      </div>

      <div class="email-advanced-filters pt-2">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label mb-0 small text-muted">{i18n?.t('emails.filter.status')}</label>
            <select
              class="form-select form-select-sm"
              value={advFilters.status || ''}
              onChange={e => handleAdvancedFilter('status', e.target.value)}
            >
              <option value="">{i18n?.t('helpers.all')}</option>
              <option value="open">{i18n?.t('emails.status.open')}</option>
              <option value="inprogress">{i18n?.t('emails.status.inprogress')}</option>
              <option value="waiting_for_customer">{i18n?.t('emails.status.waiting_for_customer')}</option>
              <option value="closed">{i18n?.t('emails.status.closed')}</option>
            </select>
          </div>
          <div class="col-auto">
            <label class="form-label mb-0 small text-muted">{i18n?.t('emails.all_responsible')}</label>
            <select
              class="form-select form-select-sm"
              value={advFilters.responsible_id || ''}
              onChange={e => {
                store.setAdvancedFilter('responsible_id', e.target.value)
                store.fetchEmails(1)
              }}
            >
              <option value="">{i18n?.t('emails.filters.all_responsible')}</option>
              {(store.peopleWithUser.value || []).map(person => (
                <option key={person.id} value={person.id}>{person.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}
